import * as S from './styles.ts';
import { ReactElement, useRef, useState } from 'react';
import { Popup } from '@template/components';
import ChevronDown from '@template/icons/ChevronDown.tsx';
import { DynamicIcon } from '@template/components';

interface RowType {
  rowName: string;
  values: (string | boolean)[];
  popover?: ReactElement[];
}

export interface PlanTableProps {
  header?: string;
  rows: RowType[];
}

const handleValue = (value: boolean | string) => {
  if (typeof value === 'boolean') {
    return value ? <DynamicIcon name='Check' /> : '';
  }
  return value;
};

const Column = ({
  value,
  popover,
}: {
  value?: string | boolean;
  popover?: ReactElement;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<any>(null);

  const infoContainerRef = useRef<HTMLDivElement>(null);

  const handleMouseOut = (e) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const eContains =
        e?.relatedTarget && infoContainerRef.current.contains(e.relatedTarget);

      if (eContains) {
        clearTimeout(timerRef.current);
        return;
      }
      setIsOpen(false);
    }, 500);
  };

  if (popover)
    return (
      <Popup
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        popupContent={
          <div
            onMouseOver={(e) => {
              clearTimeout(timerRef.current);
            }}
            onMouseOut={handleMouseOut}
          >
            {popover}
          </div>
        }
        triggerProps={
          {
            as: S.Td,
            onMouseOver: () => setIsOpen(true),
            onMouseOut: handleMouseOut,
          } as any
        }
        unstyledTrigger
        placement='bottom'
        offset={[0, 0]}
      >
        <S.InfoContainer ref={infoContainerRef}>
          {handleValue(value)}
          <ChevronDown
            style={{
              rotate: isOpen ? '180deg' : '0deg',
            }}
          />
        </S.InfoContainer>
      </Popup>
    );

  return (
    <S.Td>
      <S.InfoContainer>{handleValue(value)}</S.InfoContainer>
    </S.Td>
  );
};

const Row = ({ row }: { row: RowType }) => {
  return (
    <S.Tr>
      <S.Td>
        <S.FeatureName>{row.rowName}</S.FeatureName>
      </S.Td>
      {row.values.map((value, i) => (
        <Column key={i} value={value} popover={row.popover?.[i]} />
      ))}
    </S.Tr>
  );
};

export const PlanTable = ({ header, rows }: PlanTableProps) => {
  return (
    <S.Table>
      <S.TableHeader>
        <tr>
          <th>{header}</th>
        </tr>
      </S.TableHeader>
      <tbody>{rows?.map((row, i) => <Row row={row} key={i} />)}</tbody>
    </S.Table>
  );
};
